import { Divider } from '@/components/Divider'
import { Segmented } from '@/components/Segmented'
import * as D from '@/domain'
import * as M from '@/materials'
import { BallotVariant, CantonIcon } from '@/materials'
import * as React from 'react'
import { defineMessages } from 'react-intl'
import styled, { css } from 'styled-components/macro'
import * as R from '../../apps/ballot/standalone/routes'
import { useEnv } from '../../lib/env'
import { useCantonalBallotDocument, useCantonalBallotsCollection, useFirestoreSnapshot } from '../../lib/index'
import { cantonDescriptor } from '../../locale/messages'
import { Tile } from './internal'
import * as firestore from 'firebase/firestore'
import * as F from 'fp-ts'

const Root = 'div'

const messageDescriptors = defineMessages({
  cantons: {
    id: 'compositions.cantonal-ballots-section.title',
    defaultMessage: 'Kanton {canton}',
  },
})

// ----------------------------------------------------------------------------

const Flag = styled('div')`
  display: inline-block;
  width: 24px;
  height: 24px;
  box-shadow: 0 0 1px 0 rgba(0, 0, 0, 0.5);
  margin-right: 15px;
  svg {
    width: 100%;
    height: 100%;
  }
`

interface Props extends React.ComponentPropsWithoutRef<typeof Root> {
  popularVoteId: string
  cantonId: D.CantonId
}

function CantonalBallotsSummary(props: Props) {
  const { intl, db } = useEnv()

  const { popularVoteId, cantonId, ...rest } = props

  const title = intl.formatMessage(messageDescriptors.cantons, {
    canton: intl.formatMessage(cantonDescriptor(cantonId, 'full')),
  })

  const lead = (function useSilence() {
    const canton = useFirestoreSnapshot(firestore.doc(db, 'popularVotes', popularVoteId, 'cantons', cantonId))

    if (canton) {
      return F.function.pipe(
        D.optionMultiLocaleString(intl, (canton.data() || {}).lead || {}),
        F.option.getOrElse(() => ''),
      )
    } else {
      return ''
    }
  })()

  const ballotIds = (function useSilence() {
    const ballots = useCantonalBallotsCollection({ popularVoteId, cantonId })

    /*
     * We know there is at least one ballot in this canton, and ballot IDs are
     * sequential, starting with "1". So we return ["1"] to reserve at least that
     * much vertical space on the page and prevent it from jumping around too
     * much during loading.
     */
    if (!ballots) {
      return ['1']
    } else {
      return ballots.map((x) => x.id)
    }
  })()

  return (
    <Root {...rest}>
      <Divider variant="strong" css={styles.divider as any} />

      <h3 css={styles.heading as any} id={cantonId}>
        <Flag>
          <CantonIcon name={cantonId} />
        </Flag>
        {title}
      </h3>

      {lead && <div css={styles.lead as any}>{lead}</div>}

      <Segmented css={styles.tiles as any}>
        {ballotIds.map((id) => (
          <Tile_ key={id} popularVoteId={popularVoteId} cantonId={cantonId} ballotId={id} />
        ))}
      </Segmented>
    </Root>
  )
}

const styles = {
  root: css``,

  divider: css`
    margin-bottom: 20px;
  `,

  heading: css`
    display: flex;
    align-items: center;

    ${M.fontHeading4_};

    text-transform: uppercase;

    margin: 0;
  `,

  lead: css`
    ${M.fontLead4_};
    margin-top: 1em;
  `,

  tiles: css`
    margin-top: 25px;
  `,

  tileWrapper: css`
    justify-self: stretch;

    display: flex;
    flex-direction: column;
    padding: var(--gutter-half) var(--gutter-half) 0;

    grid-column: span 12;

    @media only screen and (min-width: 600px) {
      grid-column: span 6;
    }
    @media only screen and (min-width: 700px) {
      grid-column: span 4;
    }
    @media only screen and (min-width: 900px) {
      grid-column: span 3;
    }
  `,

  tile: css`
    padding-bottom: var(--gutter-half);
    flex: 1;
  `,
}

export default CantonalBallotsSummary

export function Tile_(props: { popularVoteId: string; cantonId: D.CantonId; ballotId: string }) {
  const { intl, history, db } = useEnv()

  const snapshots = (function useSilence() {
    const popularVote = firestore.doc(db, 'popularVotes', props.popularVoteId)
    const canton = firestore.doc(popularVote, 'cantons', props.cantonId)
    const ballot = firestore.doc(canton, 'ballots', props.ballotId)
    const municipalities = firestore.collection(ballot, 'municipalities')

    return {
      canton: useFirestoreSnapshot(canton),
      cantonalBallotDocument: useCantonalBallotDocument({ ...props }),
      municipalities: useFirestoreSnapshot(municipalities),
    }
  })()

  if (!snapshots.canton || !snapshots.cantonalBallotDocument || !snapshots.municipalities) {
    return (
      <div css={styles.tileWrapper as any}>
        <Tile
          css={styles.tile as any}
          popularVoteId={props.popularVoteId}
          name=""
          ballotVariant="default"
          sideLabels={D.emptySideLabels}
          cantonState={{ id: props.cantonId, state: 'pre', info: {} }}
          municipalities={[]}
        />
        <Divider />
      </div>
    )
  }

  const name = D.pickMultiLocaleString(intl, snapshots.cantonalBallotDocument.name, '<cantonal ballot name>')

  const ballotVariant: BallotVariant = D.isTiebreakQuestion(props.ballotId, snapshots.cantonalBallotDocument)
    ? 'tieBreak'
    : 'default'

  const cantonState: D.CantonState = {
    id: props.cantonId,
    state: D.docToBallotState(snapshots.cantonalBallotDocument),
    info: {},
  }

  const municipalities = snapshots.municipalities.docs.map((doc) => ({
    id: D.mkMunicipalityId(+doc.id),
    name: doc.data().name,
    ballotState: D.docToBallotState(doc.data()),
  }))

  return (
    <div css={styles.tileWrapper as any}>
      <Tile
        css={styles.tile as any}
        popularVoteId={props.popularVoteId}
        sideLabels={snapshots.cantonalBallotDocument.sideLabels ?? D.emptySideLabels}
        name={name}
        ballotVariant={ballotVariant}
        cantonState={cantonState}
        municipalities={municipalities}
        href={R.cantonalBallot(props.popularVoteId, props.cantonId, props.ballotId)}
        onClick={() => {
          history.push(R.cantonalBallot(props.popularVoteId, props.cantonId, props.ballotId))
        }}
      />
      <Divider />
    </div>
  )
}
